import { Badge, VStack } from '@chakra-ui/react';
import { ReactNode } from 'react';

import { IconType } from 'components/Icon';
import Text from 'components/Text';
import { Plan_Type_Enum } from 'types/databaseEnums';

interface PlanDetails {
  order: number;
  title: string;
  icon: IconType;
  tokens: number;
  discordText: string | null;
  color: 'cyan.200' | 'purple.200' | 'orange.200';
  border: 'normal' | 'fancy';
  badge: ReactNode | null;
  badgeIcon: IconType | null;
  info: (price: string) => JSX.Element;
  description: ReactNode;
}

interface IYearlyInfoBoxProps {
  totalPrice: number;
}

export const YearlyInfoBox = ({ totalPrice }: IYearlyInfoBoxProps) => {
  return (
    <Text fontSize="small" fontWeight="regular" opacity="secondary">
      ${totalPrice.toFixed(2).replace(/\.00$/, '')} billed yearly
    </Text>
  );
};

export const plans: Record<Plan_Type_Enum, PlanDetails> = {
  Lifetime: {
    order: 3,
    title: 'Lifetime',
    icon: 'LifetimePlan',
    tokens: 15,
    discordText: 'Lifetime Legends channel',
    color: 'cyan.200',
    border: 'fancy',
    badge: (
      <Badge px={1} py={0.5} ml={2} borderRadius="md" backgroundColor="whiteAlpha.200">
        <Text fontSize="micro" textTransform="none" fontWeight="regular">
          Most popular
        </Text>
      </Badge>
    ),
    badgeIcon: 'LifetimeBadge',
    info: () => (
      <Text fontSize="small" fontWeight="semibold" opacity="secondary">
        One-time payment
      </Text>
    ),
    description: (
      <VStack spacing={0}>
        <span>15 BigTokens, priority support,</span>
        <span>lifetime access & all future updates</span>
      </VStack>
    ),
  },
  Yearly: {
    order: 2,
    title: 'Yearly',
    icon: 'YearlyPlan',
    tokens: 10,
    discordText: 'Yearly Daredevils channel',
    color: 'purple.200',
    border: 'normal',
    badge: null,
    badgeIcon: 'YearlySubscriberBadge',
    info: (price: string) => (
      <VStack spacing={0}>
        <Text fontSize="small" fontWeight="semibold" opacity="secondary">
          Billed yearly
        </Text>
        <Text fontSize="small" fontWeight="semibold" opacity="secondary">
          or ${price} billed monthly
        </Text>
      </VStack>
    ),
    description: (
      <VStack spacing={0}>
        <span>10 BigTokens, save 33%,</span>
        <span>Yearly Daredevils Discord</span>
      </VStack>
    ),
  },
  Monthly: {
    order: 1,
    title: 'Monthly',
    icon: 'MonthlyPlan',
    tokens: 5,
    discordText: 'Community access',
    color: 'orange.200',
    border: 'normal',
    badge: null,
    badgeIcon: 'MonthlySubscriberBadge',
    info: () => (
      <Text fontSize="small" fontWeight="regular" opacity="secondary">
        Billed every month
      </Text>
    ),
    description: (
      <VStack spacing={0}>
        <span>5 BigTokens, flexible billing,</span>
        <span>cancel anytime</span>
      </VStack>
    ),
  },
  Free: {
    order: 0,
    title: 'Free',
    icon: 'FreePlan',
    tokens: 0,
    discordText: null,
    border: 'normal',
    color: 'cyan.200',
    badge: null,
    badgeIcon: null,
    info: () => (
      <Text fontSize="small" fontWeight="semibold" opacity="secondary">
        7-day trial
      </Text>
    ),
    description: (
      <VStack spacing={0}>
        <span>Try everything free</span>
        <span>for 7 days</span>
      </VStack>
    ),
  },
};

export const DATE_FORMAT = 'do MMM yyyy';
export const DATE_FORMAT_WITH_TIME = 'do MMM yyyy, h:mm a';
